import * as React from "react";
import { cn } from "@/lib/utils";
import { Check, X, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { useTranslations } from "use-intl";
export type OptionType = {
  label: string;
  value: string;
};
interface MultiSelectProps {
  options: OptionType[];
  selected: OptionType[];
  onChange: React.Dispatch<React.SetStateAction<OptionType[]>>;
  className?: string;
  extraStyles?: string;
  isServer?: boolean;
  placeholder?: string;
  onQueryChange?: (query: string) => void;
}
function MultiSelect({
  options,
  selected,
  onChange,
  className,
  onQueryChange,
  isServer = false,
  extraStyles = "",
  placeholder,
  ...props
}: MultiSelectProps) {
  const t = useTranslations();
  const [open, setOpen] = React.useState(false);
  const handleUnselect = (item: OptionType) => {
    onChange(selected.filter(i => i.value !== item.value));
  };
  return <Popover open={open} onOpenChange={setOpen} {...props} data-sentry-element="Popover" data-sentry-component="MultiSelect" data-sentry-source-file="multi-select.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="multi-select.tsx">
        <Button variant="outline" role="combobox" aria-expanded={open} className={cn(`w-full rounded-3xl ${selected.length > 1 ? "h-full" : "h-12"}`, extraStyles)} onClick={() => setOpen(!open)} data-sentry-element="Button" data-sentry-source-file="multi-select.tsx">
          <div className="flex flex-wrap gap-1">
            {selected.length === 0 && <span>{placeholder || t("placeholder_search_20230807140502")}</span>}
            {selected.map(item => <Badge variant="secondary" key={item.value} className="mb-1 mr-1" onClick={() => handleUnselect(item)}>
                {item.label}
                <button className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2" onKeyDown={e => {
              if (e.key === "Enter") {
                handleUnselect(item);
              }
            }} onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
            }} onClick={() => handleUnselect(item)}>
                  <X className="size-3 text-muted-foreground hover:text-foreground" />
                </button>
              </Badge>)}
          </div>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" data-sentry-element="ChevronsUpDown" data-sentry-source-file="multi-select.tsx" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" data-sentry-element="PopoverContent" data-sentry-source-file="multi-select.tsx">
        <Command className={className} shouldFilter={!isServer} data-sentry-element="Command" data-sentry-source-file="multi-select.tsx">
          <CommandInput placeholder={t("placeholder_search_20230807140502")} onValueChange={onQueryChange} data-sentry-element="CommandInput" data-sentry-source-file="multi-select.tsx" />
          <CommandList data-sentry-element="CommandList" data-sentry-source-file="multi-select.tsx">
            <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="multi-select.tsx">{t("src_components_ui_multi_select_tsx_0")}</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto" data-sentry-element="CommandGroup" data-sentry-source-file="multi-select.tsx">
              {options.map(option => <CommandItem key={option.value} onSelect={() => {
              onChange(selected.map(s => s.value).includes(option.value) ? selected.filter(item => item.value !== option.value) : [...selected, option]);
              setOpen(true);
            }}>
                  <Check className={cn("mr-2 h-4 w-4", selected.map(s => s.value).includes(option.value) ? "opacity-100" : "opacity-0")} />
                  {option.label}
                </CommandItem>)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>;
}
export { MultiSelect };